import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { teachersApi, staffApi, staffPinApi, teacherPinApi, teachersImportApi, staffImportApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, PencilSimple, Trash, ChalkboardTeacher, CookingPot, Bus, Key, Copy, Check, GraduationCap, Broom, Briefcase, FilePdf, FileXls, Camera, X as XIcon } from '@phosphor-icons/react';
import StaffBenefitsSection from '../components/staff/StaffBenefitsSection';
import { StaffPinDialog } from '../components/staff/StaffPinDialog';
import { StaffFormDialog } from '../components/staff/StaffFormDialog';
import { DatePicker } from '../components/ui/date-picker';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { compressImage, fmtBytes } from '../lib/imageCompress';
import ImportWizard from '../components/ImportWizard';

const CONTRACT_TYPES = [
  { value: 'CDI', key: 'contractCDI' },
  { value: 'CDD', key: 'contractCDD' },
  { value: 'Stagiaire', key: 'contractIntern' },
  { value: 'Vacataire', key: 'contractVacataire' },
  { value: 'Autre', key: 'contractOther' },
];

const Staff = () => {
  const { t, language } = useLanguage();
  const [teachers, setTeachers] = useState([]);
  const [canteenStaff, setCanteenStaff] = useState([]);
  const [transportStaff, setTransportStaff] = useState([]);
  const [educators, setEducators] = useState([]);
  const [cleaningStaff, setCleaningStaff] = useState([]);
  const [directionStaff, setDirectionStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teacherDialogOpen, setTeacherDialogOpen] = useState(false);
  // iter52 — Teachers import wizard (csv + xlsx + mapping)
  const [teachersImportOpen, setTeachersImportOpen] = useState(false);
  // iter53 — Staff import wizard (per staff_type)
  const [staffImportOpen, setStaffImportOpen] = useState(false);
  const [staffImportType, setStaffImportType] = useState('canteen');
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteType, setDeleteType] = useState('');

  // PIN dialog for mobile staff/teacher
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinTarget, setPinTarget] = useState(null);
  const [pinKind, setPinKind] = useState('staff'); // 'staff' (4) | 'teacher' (6)
  const [pinValue, setPinValue] = useState('');
  const [pinSaving, setPinSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const openPinDialog = (target, kind = 'staff') => {
    setPinTarget(target);
    setPinKind(kind);
    const len = kind === 'teacher' ? 6 : 4;
    const min = Math.pow(10, len - 1);
    const max = Math.pow(10, len) - 1;
    setPinValue(String(Math.floor(min + Math.random() * (max - min))));
    setCopied(false);
    setPinDialogOpen(true);
  };

  const savePin = async () => {
    const requiredLen = pinKind === 'teacher' ? 6 : 4;
    if (!new RegExp(`^\\d{${requiredLen}}$`).test(pinValue)) {
      toast.error(language === 'en'
        ? `PIN must be ${requiredLen} digits`
        : `Le PIN doit contenir ${requiredLen} chiffres`);
      return;
    }
    setPinSaving(true);
    try {
      if (pinKind === 'teacher') {
        await teacherPinApi.setPin({ teacher_id: pinTarget.id, pin: pinValue });
      } else {
        await staffPinApi.setPin({ staff_id: pinTarget.id, pin: pinValue });
      }
      toast.success(language === 'en' ? 'PIN saved' : 'PIN enregistré');
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'en' ? 'Error' : 'Erreur'));
    } finally { setPinSaving(false); }
  };

  const copyCredentials = () => {
    if (!pinTarget) return;
    const idLabel = pinKind === 'teacher' ? 'teacher_id' : 'staff_id';
    const txt = `${idLabel}: ${pinTarget.id}\nPIN: ${pinValue}\nURL: ${window.location.origin}/m/login`;
    navigator.clipboard.writeText(txt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error('Copy failed'));
  };

  const [teacherForm, setTeacherForm] = useState({
    first_name: '', last_name: '', contact: '', email: '', subject: '', salary: '',
    function: '', contract_type: '', address: '', birthdate: '', hire_date: '', photo_base64: '',
    iban: '', bic: '', bank_name: '',
    transport_allowance: '', other_benefits: [],
  });

  const [staffForm, setStaffForm] = useState({
    first_name: '', last_name: '', contact: '', email: '', staff_type: 'canteen', salary: '',
    function: '', contract_type: '', address: '', birthdate: '', hire_date: '', photo_base64: '',
    iban: '', bic: '', bank_name: '',
    transport_allowance: '', other_benefits: [],
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [teachersRes, canteenRes, transportRes, educatorsRes, cleaningRes, directionRes] = await Promise.all([
        teachersApi.getAll(),
        staffApi.getAll({ staff_type: 'canteen' }),
        staffApi.getAll({ staff_type: 'transport' }),
        staffApi.getAll({ staff_type: 'educator' }),
        staffApi.getAll({ staff_type: 'cleaning' }),
        staffApi.getAll({ staff_type: 'direction' }),
      ]);
      setTeachers(teachersRes.data);
      setCanteenStaff(canteenRes.data);
      setTransportStaff(transportRes.data);
      setEducators(educatorsRes.data);
      setCleaningStaff(cleaningRes.data);
      setDirectionStaff(directionRes.data);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleTeacherSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...teacherForm,
        salary: parseFloat(teacherForm.salary) || 0,
        transport_allowance: parseFloat(teacherForm.transport_allowance) || 0,
        other_benefits: (teacherForm.other_benefits || [])
          .filter((b) => (b.label || '').trim())
          .map((b) => ({ label: b.label, amount: parseFloat(b.amount) || 0 })),
      };
      if (selectedItem) {
        await teachersApi.update(selectedItem.id, payload);
      } else {
        await teachersApi.create(payload);
      }
      toast.success(t('success'));
      setTeacherDialogOpen(false);
      resetTeacherForm();
      loadData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...staffForm,
        salary: parseFloat(staffForm.salary) || 0,
        transport_allowance: parseFloat(staffForm.transport_allowance) || 0,
        other_benefits: (staffForm.other_benefits || [])
          .filter((b) => (b.label || '').trim())
          .map((b) => ({ label: b.label, amount: parseFloat(b.amount) || 0 })),
      };
      if (selectedItem) {
        await staffApi.update(selectedItem.id, payload);
      } else {
        await staffApi.create(payload);
      }
      toast.success(t('success'));
      setStaffDialogOpen(false);
      resetStaffForm();
      loadData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleDelete = async () => {
    try {
      if (deleteType === 'teacher') {
        await teachersApi.delete(selectedItem.id);
      } else {
        await staffApi.delete(selectedItem.id);
      }
      toast.success(t('success'));
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      loadData();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const resetTeacherForm = () => {
    setSelectedItem(null);
    setTeacherForm({
      first_name: '', last_name: '', contact: '', email: '', subject: '', salary: '',
      function: '', contract_type: '', address: '', birthdate: '', hire_date: '', photo_base64: '',
      iban: '', bic: '', bank_name: '',
      transport_allowance: '', other_benefits: [],
    });
  };

  const resetStaffForm = () => {
    setSelectedItem(null);
    setStaffForm({
      first_name: '', last_name: '', contact: '', email: '', staff_type: 'canteen', salary: '',
      function: '', contract_type: '', address: '', birthdate: '', hire_date: '', photo_base64: '',
      iban: '', bic: '', bank_name: '',
      transport_allowance: '', other_benefits: [],
    });
  };

  const handleEditTeacher = (teacher) => {
    setSelectedItem(teacher);
    setTeacherForm({
      first_name: teacher.first_name || '',
      last_name: teacher.last_name || '',
      contact: teacher.contact || '',
      email: teacher.email || '',
      subject: teacher.subject || '',
      salary: teacher.salary?.toString() || '',
      function: teacher.function || '',
      contract_type: teacher.contract_type || '',
      address: teacher.address || '',
      birthdate: (teacher.birthdate || '').slice(0, 10),
      hire_date: (teacher.hire_date || '').slice(0, 10),
      photo_base64: teacher.photo_base64 || '',
      iban: teacher.iban || '',
      bic: teacher.bic || '',
      bank_name: teacher.bank_name || '',
      transport_allowance: teacher.transport_allowance?.toString() || '',
      other_benefits: teacher.other_benefits || [],
    });
    setTeacherDialogOpen(true);
  };

  const handleEditStaff = (staff) => {
    setSelectedItem(staff);
    setStaffForm({
      first_name: staff.first_name || '',
      last_name: staff.last_name || '',
      contact: staff.contact || '',
      staff_type: staff.staff_type || 'canteen',
      salary: staff.salary?.toString() || '',
      function: staff.function || '',
      contract_type: staff.contract_type || '',
      address: staff.address || '',
      birthdate: (staff.birthdate || '').slice(0, 10),
      hire_date: (staff.hire_date || '').slice(0, 10),
      photo_base64: staff.photo_base64 || '',
      iban: staff.iban || '',
      bic: staff.bic || '',
      bank_name: staff.bank_name || '',
      email: staff.email || '',
      transport_allowance: staff.transport_allowance?.toString() || '',
      other_benefits: staff.other_benefits || [],
    });
    setStaffDialogOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  // Photo upload helper — delegates to the shared canvas compressor
  const handlePhotoUpload = async (file, setter, currentForm) => {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      toast.error(language === 'fr' ? 'Format non supporté (JPG/PNG/WebP)' : 'Unsupported format');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === 'fr' ? 'Photo trop volumineuse (max 5 Mo avant compression)' : 'Photo too large (max 5 MB before compression)');
      return;
    }
    try {
      // Staff ID cards use 320px photos → override the default 512 max
      const result = await compressImage(file, { maxSide: 320, quality: 0.85 });
      setter({ ...currentForm, photo_base64: result.dataUrl });
      if (!result.skipped) {
        toast.success(
          language === 'fr'
            ? `Photo compressée : ${fmtBytes(result.originalSize)} → ${fmtBytes(result.compressedSize)}`
            : `Photo compressed: ${fmtBytes(result.originalSize)} → ${fmtBytes(result.compressedSize)}`,
        );
      }
    } catch {
      toast.error(language === 'fr' ? 'Lecture ou compression impossible' : 'Read or compression failed');
    }
  };

  const downloadXlsx = async (apiCall, filename) => {
    try {
      const res = await apiCall();
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(language === 'fr' ? 'Export Excel téléchargé' : 'Excel exported');
    } catch (e) {
      toast.error(language === 'fr' ? 'Échec export Excel' : 'Excel export failed');
    }
  };

  const downloadPdf = async (apiCall, fullName) => {
    try {
      const res = await apiCall();
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fiche_${(fullName || 'employe').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(language === 'fr' ? 'Erreur lors du téléchargement' : 'Download failed');
    }
  };

  // Reusable staff table renderer (for canteen/transport/educator/cleaning/direction)
  const renderStaffTable = (rows, addTestId, addBtnLabel, defaultStaffType) => (
    <div className="space-y-4">
      <div className="flex justify-end gap-2 flex-wrap">
        <Button
          variant="outline"
          onClick={async () => {
            try {
              const { data } = await staffImportApi.template(defaultStaffType);
              const url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv;charset=utf-8' }));
              const a = document.createElement('a');
              a.href = url; a.download = `modele-${defaultStaffType}.csv`;
              document.body.appendChild(a); a.click(); a.remove();
              window.URL.revokeObjectURL(url);
              toast.success(language === 'fr' ? 'Modèle téléchargé' : 'Template downloaded');
            } catch (e) {
              toast.error(language === 'fr' ? 'Erreur téléchargement modèle' : 'Template download failed');
            }
          }}
          className="border-[#E2E8F0]"
          data-testid={`download-staff-template-${defaultStaffType}`}
        >
          <FileXls size={16} className="mr-2" />
          {language === 'fr' ? 'Modèle CSV' : 'CSV template'}
        </Button>
        <Button
          variant="outline"
          onClick={() => { setStaffImportType(defaultStaffType); setStaffImportOpen(true); }}
          className="border-[#E2E8F0]"
          data-testid={`import-staff-${defaultStaffType}`}
        >
          <Plus size={16} className="mr-2" />
          {language === 'fr' ? 'Importer CSV/Excel' : 'Import CSV/Excel'}
        </Button>
        {rows.length > 0 && (
          <>
            <Button
              variant="outline"
              onClick={() => downloadXlsx(() => staffApi.exportXlsx(defaultStaffType), `registre_${defaultStaffType}.xlsx`)}
              className="border-[#15803D] text-[#15803D] hover:bg-[#DCFCE7]"
              data-testid={`xlsx-export-${defaultStaffType}`}
              title={language === 'fr' ? 'Exporter en Excel' : 'Export to Excel'}
            >
              <FileXls size={16} className="mr-2" />
              {language === 'fr' ? 'Export Excel' : 'Export Excel'}
            </Button>
            <Button
              variant="outline"
              onClick={() => downloadPdf(() => staffApi.identityCardsBulkPdf(defaultStaffType), `fiches_${defaultStaffType}`)}
              className="border-[#B45309] text-[#B45309] hover:bg-[#FEF3C7]"
              data-testid={`bulk-pdf-${defaultStaffType}`}
              title={language === 'fr' ? 'Exporter toutes les fiches' : 'Export all sheets'}
            >
              <FilePdf size={16} className="mr-2" />
              {language === 'fr' ? 'Exporter toutes les fiches' : 'Export all sheets'}
            </Button>
          </>
        )}
        <Button
          onClick={() => { resetStaffForm(); setStaffForm(f => ({ ...f, staff_type: defaultStaffType })); setStaffDialogOpen(true); }}
          className="bg-[#0052FF] hover:bg-[#0043D1]"
          data-testid={addTestId}
        >
          <Plus size={18} className="mr-2" />
          {addBtnLabel}
        </Button>
      </div>
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#E2E8F0]">
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('lastName')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('firstName')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('function')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('contractType')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('contact')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('salary')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A] text-right">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-[#64748B]">{t('noData')}</TableCell>
                </TableRow>
              ) : (
                rows.map((staff) => (
                  <TableRow key={staff.id} className="border-[#E2E8F0] hover:bg-[#F1F5F9]" data-testid={`${defaultStaffType}-row-${staff.id}`}>
                    <TableCell className="font-manrope">{staff.last_name}</TableCell>
                    <TableCell className="font-manrope">{staff.first_name}</TableCell>
                    <TableCell className="font-manrope text-[#475569]">{staff.function || '—'}</TableCell>
                    <TableCell className="font-manrope text-[#475569]">{staff.contract_type || '—'}</TableCell>
                    <TableCell className="font-manrope">{staff.contact}</TableCell>
                    <TableCell className="font-manrope">{formatCurrency(staff.salary)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => downloadPdf(() => staffApi.identityCardPdf(staff.id), `${staff.last_name}_${staff.first_name}`)} className="hover:bg-[#FEF3C7] text-[#B45309]" title={language === 'fr' ? 'Exporter fiche PDF' : 'Export PDF'} data-testid={`pdf-staff-${staff.id}`}>
                        <FilePdf size={18} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEditStaff(staff)} className="hover:bg-[#EFF6FF] text-[#0052FF]" title={t('edit') || 'Edit'} data-testid={`edit-staff-${staff.id}`}>
                        <PencilSimple size={18} />
                      </Button>
                      {defaultStaffType !== 'direction' && (
                        <Button variant="ghost" size="sm" onClick={() => openPinDialog(staff)} className="hover:bg-[#EFF6FF] text-[#0052FF]" title="PIN" data-testid={`pin-btn-${staff.id}`}>
                          <Key size={16} />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => { setSelectedItem(staff); setDeleteType('staff'); setDeleteDialogOpen(true); }} className="hover:bg-[#FEF2F2] text-[#EF4444]">
                        <Trash size={18} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="staff-page">
      <h1 className="font-outfit text-2xl font-bold text-[#0F172A]">{t('staff')}</h1>

      <Tabs defaultValue="teachers" className="space-y-6">
        <TabsList className="bg-[#F1F5F9] flex-wrap h-auto">
          {/* Direction group */}
          <span className="text-[10px] font-manrope text-[#94A3B8] uppercase px-2 self-center tracking-wider">
            {t('directionGroup')}
          </span>
          <TabsTrigger value="direction" className="data-[state=active]:bg-white" data-testid="tab-direction">
            <Briefcase size={18} className="mr-2" />
            {t('directionStaff')}
          </TabsTrigger>

          <span className="border-l border-[#CBD5E1] mx-2 self-stretch" />

          {/* Pédagogique group */}
          <span className="text-[10px] font-manrope text-[#94A3B8] uppercase px-2 self-center tracking-wider">
            {t('pedagogicalGroup')}
          </span>
          <TabsTrigger value="teachers" className="data-[state=active]:bg-white" data-testid="tab-teachers">
            <ChalkboardTeacher size={18} className="mr-2" />
            {t('teachers')}
          </TabsTrigger>
          <TabsTrigger value="educators" className="data-[state=active]:bg-white" data-testid="tab-educators">
            <GraduationCap size={18} className="mr-2" />
            {t('educators')}
          </TabsTrigger>

          <span className="border-l border-[#CBD5E1] mx-2 self-stretch" />

          {/* Services group */}
          <span className="text-[10px] font-manrope text-[#94A3B8] uppercase px-2 self-center tracking-wider">
            {t('servicesGroup')}
          </span>
          <TabsTrigger value="canteen" className="data-[state=active]:bg-white" data-testid="tab-canteen">
            <CookingPot size={18} className="mr-2" />
            {t('canteenStaff')}
          </TabsTrigger>
          <TabsTrigger value="transport" className="data-[state=active]:bg-white" data-testid="tab-transport">
            <Bus size={18} className="mr-2" />
            {t('transportStaff')}
          </TabsTrigger>
          <TabsTrigger value="cleaning" className="data-[state=active]:bg-white" data-testid="tab-cleaning">
            <Broom size={18} className="mr-2" />
            {t('cleaningStaff')}
          </TabsTrigger>
        </TabsList>

        {/* Direction Tab */}
        <TabsContent value="direction">
          {renderStaffTable(directionStaff, 'add-direction-btn', t('addStaff'), 'direction')}
        </TabsContent>

        {/* Teachers Tab */}
        <TabsContent value="teachers" className="space-y-4">
          <div className="flex justify-end gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const { data } = await teachersImportApi.template();
                  const url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv;charset=utf-8' }));
                  const a = document.createElement('a');
                  a.href = url; a.download = 'modele-enseignants.csv';
                  document.body.appendChild(a); a.click(); a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success(language === 'fr' ? 'Modèle téléchargé' : 'Template downloaded');
                } catch (e) {
                  toast.error(language === 'fr' ? 'Erreur téléchargement modèle' : 'Template download failed');
                }
              }}
              className="border-[#E2E8F0]"
              data-testid="download-teachers-template-btn"
              title={language === 'fr' ? 'Télécharger un modèle CSV' : 'Download CSV template'}
            >
              <FileXls size={16} className="mr-2" />
              {language === 'fr' ? 'Modèle CSV' : 'CSV template'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setTeachersImportOpen(true)}
              className="border-[#E2E8F0]"
              data-testid="import-teachers-btn"
              title={language === 'fr' ? 'Importer CSV/Excel enseignants' : 'Import CSV/Excel teachers'}
            >
              <Plus size={16} className="mr-2" />
              {language === 'fr' ? 'Importer CSV/Excel' : 'Import CSV/Excel'}
            </Button>
            {teachers.length > 0 && (
              <>
                <Button
                  variant="outline"
                  onClick={() => downloadXlsx(() => teachersApi.exportXlsx(), 'registre_enseignants.xlsx')}
                  className="border-[#15803D] text-[#15803D] hover:bg-[#DCFCE7]"
                  data-testid="xlsx-export-teachers"
                  title={language === 'fr' ? 'Exporter en Excel' : 'Export to Excel'}
                >
                  <FileXls size={16} className="mr-2" />
                  {language === 'fr' ? 'Export Excel' : 'Export Excel'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadPdf(() => teachersApi.identityCardsBulkPdf(), 'fiches_enseignants')}
                  className="border-[#B45309] text-[#B45309] hover:bg-[#FEF3C7]"
                  data-testid="bulk-pdf-teachers"
                  title={language === 'fr' ? 'Exporter toutes les fiches' : 'Export all sheets'}
                >
                  <FilePdf size={16} className="mr-2" />
                  {language === 'fr' ? 'Exporter toutes les fiches' : 'Export all sheets'}
                </Button>
              </>
            )}
            <Button
              onClick={() => { resetTeacherForm(); setTeacherDialogOpen(true); }}
              className="bg-[#0052FF] hover:bg-[#0043D1]"
              data-testid="add-teacher-btn"
            >
              <Plus size={18} className="mr-2" />
              {t('addTeacher')}
            </Button>
          </div>
          <Card className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0]">
                    <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('lastName')}</TableHead>
                    <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('firstName')}</TableHead>
                    <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('subject')}</TableHead>
                    <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('function')}</TableHead>
                    <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('contractType')}</TableHead>
                    <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('contact')}</TableHead>
                    <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('salary')}</TableHead>
                    <TableHead className="font-outfit font-semibold text-[#0F172A] text-right">{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0052FF] mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : teachers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-[#64748B]">{t('noData')}</TableCell>
                    </TableRow>
                  ) : (
                    teachers.map((teacher) => (
                      <TableRow key={teacher.id} className="border-[#E2E8F0] hover:bg-[#F1F5F9]">
                        <TableCell className="font-manrope">{teacher.last_name}</TableCell>
                        <TableCell className="font-manrope">{teacher.first_name}</TableCell>
                        <TableCell className="font-manrope">{teacher.subject}</TableCell>
                        <TableCell className="font-manrope text-[#475569]">{teacher.function || '—'}</TableCell>
                        <TableCell className="font-manrope text-[#475569]">{teacher.contract_type || '—'}</TableCell>
                        <TableCell className="font-manrope">{teacher.contact}</TableCell>
                        <TableCell className="font-manrope">{formatCurrency(teacher.salary)}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => downloadPdf(() => teachersApi.identityCardPdf(teacher.id), `${teacher.last_name}_${teacher.first_name}`)} className="hover:bg-[#FEF3C7] text-[#B45309]" title={language === 'fr' ? 'Exporter fiche PDF' : 'Export PDF'} data-testid={`pdf-teacher-${teacher.id}`}>
                            <FilePdf size={18} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openPinDialog(teacher, 'teacher')} className="hover:bg-[#EFF6FF] text-[#0052FF]" title={language === 'fr' ? 'PIN mobile 6 chiffres' : '6-digit mobile PIN'} data-testid={`pin-teacher-${teacher.id}`}>
                            <Key size={18} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEditTeacher(teacher)} className="hover:bg-[#EFF6FF] text-[#0052FF]">
                            <PencilSimple size={18} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedItem(teacher); setDeleteType('teacher'); setDeleteDialogOpen(true); }} className="hover:bg-[#FEF2F2] text-[#EF4444]">
                            <Trash size={18} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Educators Tab */}
        <TabsContent value="educators">
          {renderStaffTable(educators, 'add-educator-btn', t('addStaff'), 'educator')}
        </TabsContent>

        {/* Canteen Staff Tab */}
        <TabsContent value="canteen">
          {renderStaffTable(canteenStaff, 'add-canteen-staff-btn', t('addStaff'), 'canteen')}
        </TabsContent>

        {/* Transport Staff Tab */}
        <TabsContent value="transport">
          {renderStaffTable(transportStaff, 'add-transport-staff-btn', t('addStaff'), 'transport')}
        </TabsContent>

        {/* Cleaning Staff Tab */}
        <TabsContent value="cleaning">
          {renderStaffTable(cleaningStaff, 'add-cleaning-btn', t('addStaff'), 'cleaning')}
        </TabsContent>
      </Tabs>

      {/* PIN Dialog (mobile staff) */}
      <StaffPinDialog
        open={pinDialogOpen}
        onOpenChange={setPinDialogOpen}
        pinKind={pinKind}
        pinTarget={pinTarget}
        pinValue={pinValue}
        setPinValue={setPinValue}
        copied={copied}
        copyCredentials={copyCredentials}
        savePin={savePin}
        pinSaving={pinSaving}
        language={language}
        t={t}
      />

      <StaffFormDialog
        kind="teacher"
        open={teacherDialogOpen}
        onOpenChange={setTeacherDialogOpen}
        onSubmit={handleTeacherSubmit}
        form={teacherForm}
        setForm={setTeacherForm}
        selectedItem={selectedItem}
        handlePhotoUpload={handlePhotoUpload}
        contractTypes={CONTRACT_TYPES}
        language={language}
        t={t}
      />

      <StaffFormDialog
        kind="staff"
        open={staffDialogOpen}
        onOpenChange={setStaffDialogOpen}
        onSubmit={handleStaffSubmit}
        form={staffForm}
        setForm={setStaffForm}
        selectedItem={selectedItem}
        handlePhotoUpload={handlePhotoUpload}
        contractTypes={CONTRACT_TYPES}
        language={language}
        t={t}
      />

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-outfit">{t('confirm')}</AlertDialogTitle>
            <AlertDialogDescription className="font-manrope">{t('confirmDelete')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-[#EF4444] hover:bg-red-600">{t('delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* iter52 — Teachers Import Wizard */}
      <ImportWizard
        open={teachersImportOpen}
        onOpenChange={setTeachersImportOpen}
        title={language === 'fr' ? 'Importer des enseignants' : 'Import teachers'}
        kind="teachers"
        api={{
          importPreview: (file) => teachersImportApi.import(file, { previewOnly: true }),
          importFinal: (file, opts) => teachersImportApi.import(file, opts),
        }}
        onImported={(data) => {
          if (!data.dry_run && (data.imported ?? 0) > 0) {
            toast.success(language === 'fr' ? `${data.imported} enseignant(s) importé(s)` : `${data.imported} teacher(s) imported`);
            loadData();
          }
        }}
      />

      {/* iter53 — Staff Import Wizard (canteen/transport/educator/cleaning/direction) */}
      <ImportWizard
        open={staffImportOpen}
        onOpenChange={setStaffImportOpen}
        title={language === 'fr' ? `Importer — ${staffImportType}` : `Import — ${staffImportType}`}
        kind="staff"
        api={{
          importPreview: (file) => staffImportApi.import(file, { previewOnly: true, staffType: staffImportType }),
          importFinal: (file, opts) => staffImportApi.import(file, { ...opts, staffType: staffImportType }),
        }}
        onImported={(data) => {
          if (!data.dry_run && (data.imported ?? 0) > 0) {
            toast.success(language === 'fr' ? `${data.imported} personnel(s) importé(s)` : `${data.imported} staff imported`);
            loadData();
          }
        }}
      />
    </div>
  );
};

export default Staff;
